import { sendGAEvent } from '@noths/polaris-client-google-analytics';
import type { EventsMap } from '@noths/polaris-client-next-redux';
import { isBrowser } from '@noths/polaris-client-utils';

import { PAGE_SIZE } from 'src/adapters/storeToServiceRequest/listings-api/adaptStoreToRequestBody';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import {
  PRODUCT_IMPRESSION_BATCH_SIZE,
  PRODUCT_IMPRESSION_TIMEOUT_MS,
} from 'src/redux/products/constants';
import {
  selectCurrentPage,
  selectPreviousPage,
  selectProductFromCode,
  selectProviderAttributionToken,
  selectProviderMetadata,
  selectTotalProducts,
} from 'src/redux/products/selectors/selectors';
import { requestProducts } from 'src/redux/products/thunks/requestProducts';
import { TriggerName } from 'src/redux/products/types';
import {
  selectActiveFacets,
  selectActiveSortOption,
} from 'src/redux/productsQuery/selectors/selectors';
import { getCategoryIdFromCanonicalFilters } from 'src/redux/productsQuery/utils/getCategoryIdFromCanonicalFilters';
import { actions as visibilityTrackingActions } from 'src/redux/visibilityTracking/slice';
import { getProductImpressionsTrackingEvent } from 'src/tracking/events/productImpression';
import type { ClearEcommerceObject } from 'src/types/googleAnalytics';
import { getImpressionFieldObject } from 'src/utils/googleAnalytics';
import { transformProductsToGAReturnedProductsArray } from 'src/utils/transformProductsToGAReturnedProductsArray';

type RequestProductsAction = ReturnType<(typeof requestProducts)['fulfilled']>;

const sendProductImpressionsEvent = (
  productImpressionQueue: number[],
  state: ReduxApplicationState,
) => {
  const { content, navigation, products, productsQuery } = state;
  const { pages, productsReturnedCount } = products;
  const { pageType } = navigation;
  const categoryId = getCategoryIdFromCanonicalFilters(productsQuery.canonicalFilters);

  if (Object.keys(pages).length == 0) {
    return;
  }

  sendGAEvent<ClearEcommerceObject>({
    ecommerce: null,
  });

  const productImpressionsEvent = getProductImpressionsTrackingEvent({
    eventCategory: 'Product listings',
    eventAction: 'Product Impressions',
    eventLabel: 'Impressions',
    impressions: productImpressionQueue.map((productCode) => {
      const product = selectProductFromCode(state, productCode);

      return getImpressionFieldObject(
        product!,
        pages,
        productsReturnedCount,
        productsQuery.searchTerm!,
        pageType,
        categoryId || undefined,
        {
          listSuffix: product?.isExpandedSearch ? ' - expanded' : undefined,
          partnerName: content.partner ? content.partner.name : undefined,
        },
      );
    }),
  });

  sendGAEvent(productImpressionsEvent);
};

export const GAEventsMap: EventsMap<ReduxApplicationState> = {
  [visibilityTrackingActions.sendProductImpressionBatch.type]: (prevState, _, __, dispatch) => {
    const { productImpressionQueue } = prevState.visibilityTracking;

    if (!isBrowser() || productImpressionQueue.length === 0) {
      return;
    }

    sendProductImpressionsEvent(productImpressionQueue, prevState);

    // Clear the impression queue in the store
    dispatch(visibilityTrackingActions.clearProductImpressionQueue());
  },
  [requestProducts.pending.type]: (_prevState, _nextState, _action, dispatch) => {
    dispatch(visibilityTrackingActions.sendProductImpressionBatch());
  },
  [visibilityTrackingActions.onProductVisible.type]: (prevState, nextState, _action, dispatch) => {
    const { productImpressionQueue } = nextState.visibilityTracking;

    if (productImpressionQueue.length === PRODUCT_IMPRESSION_BATCH_SIZE) {
      // We have reached the batch limit and should send the batch
      dispatch(visibilityTrackingActions.sendProductImpressionBatch());
      return;
    }

    if (!prevState.visibilityTracking.atLeastOneProductHasBecomeVisible) {
      // Send batch on an interval so we can ensure event is sent during inactivity
      setInterval(
        () => dispatch(visibilityTrackingActions.sendProductImpressionBatch()),
        PRODUCT_IMPRESSION_TIMEOUT_MS,
      );

      // Send batch if user navigates away from page
      window.onbeforeunload = () => {
        dispatch(visibilityTrackingActions.sendProductImpressionBatch());
        return; // Empty return needed to prevent dialog box showing before unload
      };
    }
  },
  [requestProducts.fulfilled.type]: (prevState, nextState, action) => {
    const { payload } = action as RequestProductsAction;
    if (!payload.products) {
      return;
    }

    const {
      products: { products },
      triggerName,
    } = payload;

    const attributionToken = selectProviderAttributionToken(nextState);
    const providerMetadata = selectProviderMetadata(nextState);

    // For filter actions, use products from nextState (filtered results)
    // For other actions, use products from action payload
    const productsToUse =
      triggerName === TriggerName.CHANGE_FACETS ? nextState.products.pages[1] || [] : products;
    const offsetCurrentPage = (selectCurrentPage(nextState) - 1) * PAGE_SIZE;
    const offsetPreviousPage = selectPreviousPage(nextState) * PAGE_SIZE;
    const returnedProducts = productsToUse
      ? transformProductsToGAReturnedProductsArray(productsToUse)
      : null;

    switch (triggerName) {
      case TriggerName.LOAD_MORE_PRODUCTS:
      case TriggerName.LOAD_PREVIOUS_PRODUCTS: {
        const productImpressionsEvent = getProductImpressionsTrackingEvent({
          eventAction:
            triggerName === TriggerName.LOAD_PREVIOUS_PRODUCTS
              ? 'Load Previous Impressions'
              : 'Load More Impressions',
          offset:
            triggerName === TriggerName.LOAD_PREVIOUS_PRODUCTS
              ? offsetPreviousPage
              : offsetCurrentPage,
          eventCategory: 'Product listings',
          eventLabel: String(selectCurrentPage(prevState)),
          ...(attributionToken && { attributionToken }),
          ...(providerMetadata && { crsFilter: providerMetadata.filter }),
          ...(returnedProducts && { returnedProducts: returnedProducts }),
        });

        sendGAEvent(productImpressionsEvent);
        break;
      }
      case TriggerName.CHANGE_FACETS: {
        const updatedTotalProducts = selectTotalProducts(nextState);
        const activeFacets = selectActiveFacets(nextState);

        // Send individual facet change event if metadata is present
        if (payload.metadata && returnedProducts) {
          const { facetTitle, optionActive, optionTitle } = payload.metadata;
          sendGAEvent({
            event: 'custom_event',
            event_category: 'Filters',
            event_action: `${optionActive ? 'select' : 'de-select'} filter ${facetTitle}`,
            event_label: optionTitle,
            ...(attributionToken && { attribution_token: attributionToken }),
            ...(providerMetadata && { crs_filter: providerMetadata.filter }),
            ...(returnedProducts && { returned_products: returnedProducts }),
            filters_applied: activeFacets.map((facet) => facet.title).join(', '),
            total_listing_items: updatedTotalProducts,
          });
        }

        // Send aggregate "applied filter info" event
        if (activeFacets.length) {
          sendGAEvent({
            event: 'custom_event',
            event_category: 'Filters',
            event_action: 'applied filter info',
            event_label: activeFacets.map((facet) => facet.title).join(', '),
            total_listing_items: updatedTotalProducts,
            offset: 0,
            ...(attributionToken && { attribution_token: attributionToken }),
            ...(providerMetadata && { crs_filter: providerMetadata.filter }),
            ...(returnedProducts && { returned_products: returnedProducts }),
          });
        }
        break;
      }
      case TriggerName.CHANGE_SORT: {
        const activeSortOption = selectActiveSortOption(prevState);

        sendGAEvent({
          event: 'custom_event',
          event_category: 'Sort Results',
          event_action: 'sort by',
          event_label: activeSortOption?.title,
          offset: 0,
          ...(attributionToken && { attribution_token: attributionToken }),
          ...(providerMetadata && { crs_filter: providerMetadata.filter }),
          ...(returnedProducts && { returned_products: returnedProducts }),
        });
      }
    }
  },
};
